/**
 * KYCAnalyticsPage — `/kyc/analytics`.
 *
 * Wired to GET /v1/kyc/analytics/summary which aggregates from kyc_profile,
 * kyc_verification, and tier_upgrade_request. Surfaces verification rate,
 * average operator turnaround, upgrades-per-month, expired verification count,
 * blocked-customer count, and per-tier KycProfile distribution.
 */
import type { JSX } from 'react';
import { AlertCircle, CheckCircle, Clock, ShieldAlert, TrendingUp } from 'lucide-react';
import { useQuery } from '@tanstack/react-query';

import { ApiError } from '../../lib/api/client';
import { getAnalyticsSummary, listSars, listWatchlistHits } from '../../lib/api/kyc';
import { PageHeader } from '../shared/StaffUI';
import { KPICard } from '../mail/shared/MailUI';

function formatDuration(seconds: number): string {
  if (!Number.isFinite(seconds) || seconds <= 0) return '—';
  if (seconds < 60) return `${Math.round(seconds)}s`;
  const minutes = seconds / 60;
  if (minutes < 60) return `${minutes.toFixed(1)}m`;
  const hours = minutes / 60;
  if (hours < 24) return `${hours.toFixed(1)}h`;
  const days = hours / 24;
  return `${days.toFixed(1)}d`;
}

function ErrorBanner({ title, message }: { readonly title: string; readonly message: string }): JSX.Element {
  return (
    <div
      className="rounded-lg bg-red-50 border border-red-200 p-3 text-xs text-red-900 space-y-1"
      data-testid="analytics-error"
    >
      <p className="font-semibold">{title}</p>
      <p className="text-red-800/90">{message}</p>
    </div>
  );
}

export function KYCAnalyticsPage(): JSX.Element {
  const summary = useQuery({
    queryKey: ['kyc', 'analytics', 'summary'],
    queryFn: () => getAnalyticsSummary(),
    retry: false,
    staleTime: 30_000,
  });

  const sars = useQuery({
    queryKey: ['kyc', 'sars', 'analytics'],
    queryFn: () => listSars({ pageSize: 100 }),
    retry: false,
  });
  const hits = useQuery({
    queryKey: ['kyc', 'watchlist', 'analytics'],
    queryFn: () => listWatchlistHits({ pageSize: 100 }),
    retry: false,
  });

  const summaryDenied =
    summary.isError &&
    summary.error instanceof ApiError &&
    (summary.error.status === 401 || summary.error.status === 403);

  const verificationRatePct = summary.data
    ? `${(summary.data.verificationRate * 100).toFixed(1)}%`
    : '—';

  return (
    <div className="space-y-6">
      <PageHeader title="KYC analytics" description="Verification + compliance metrics." />

      {summaryDenied ? (
        <ErrorBanner
          title="Permission required"
          message="Token missing Kyc/readAnalytics. Contact the platform admin to grant the role."
        />
      ) : summary.isError ? (
        <ErrorBanner title="Analytics service unreachable" message={(summary.error as Error).message} />
      ) : null}

      <div
        className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4"
        data-testid="analytics-kpi-grid"
      >
        <KPICard
          title="Verification rate"
          value={summary.isPending ? '—' : verificationRatePct}
          hint="VERIFIED / total verifications"
          icon={CheckCircle}
          tone={summary.data && summary.data.verificationRate >= 0.8 ? 'success' : 'default'}
        />
        <KPICard
          title="Avg turnaround"
          value={summary.isPending ? '—' : formatDuration(summary.data?.avgTurnaroundSeconds ?? 0)}
          hint="Resolved tier-upgrade requests"
          icon={Clock}
        />
        <KPICard
          title="Upgrades / 30d"
          value={summary.isPending ? '—' : String(summary.data?.upgradesLast30Days ?? 0)}
          hint="APPROVED + AUTO_APPROVED"
          icon={TrendingUp}
        />
        <KPICard
          title="Expired"
          value={summary.isPending ? '—' : String(summary.data?.expiredVerifications ?? 0)}
          hint="Verifications past expiry"
          icon={AlertCircle}
          tone={summary.data && summary.data.expiredVerifications > 0 ? 'warning' : 'default'}
        />
      </div>

      {/* Tier distribution */}
      <section>
        <h2 className="text-sm font-semibold text-gray-900 mb-3">KYC tier distribution</h2>
        {summary.isPending ? (
          <p className="text-sm text-muted">Loading…</p>
        ) : summary.data ? (
          <div
            className="bg-white border border-border rounded-xl overflow-hidden"
            data-testid="tier-distribution-table"
          >
            <table className="w-full text-sm">
              <thead className="bg-gray-50 text-[10px] uppercase tracking-wider text-muted">
                <tr>
                  <th className="text-left px-4 py-2">Tier</th>
                  <th className="text-right px-4 py-2">Profiles</th>
                  <th className="text-right px-4 py-2">% of total</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-border">
                {summary.data.tierDistribution.map((row) => {
                  const pct = summary.data.totalProfiles > 0
                    ? (row.count / summary.data.totalProfiles) * 100
                    : 0;
                  return (
                    <tr key={row.tier} data-testid={`tier-row-${row.tier}`}>
                      <td className="px-4 py-2 font-medium">{row.tier}</td>
                      <td className="px-4 py-2 text-right font-mono">{row.count}</td>
                      <td className="px-4 py-2 text-right text-muted">{pct.toFixed(1)}%</td>
                    </tr>
                  );
                })}
                <tr className="bg-gray-50">
                  <td className="px-4 py-2 font-semibold">Total</td>
                  <td className="px-4 py-2 text-right font-bold font-mono" data-testid="total-profiles">
                    {summary.data.totalProfiles}
                  </td>
                  <td className="px-4 py-2 text-right text-muted">100%</td>
                </tr>
              </tbody>
            </table>
          </div>
        ) : null}
      </section>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <section>
          <h2 className="text-sm font-semibold text-gray-900 mb-3">Recent SARs</h2>
          {sars.isPending ? (
            <p className="text-sm text-muted">Loading…</p>
          ) : sars.isError ? (
            <ErrorBanner title="SAR queue unavailable" message={(sars.error as Error).message} />
          ) : (
            <div className="bg-white border border-border rounded-xl p-4">
              <p className="text-sm">
                Total SARs filed: <span className="font-bold" data-testid="total-sars">{sars.data?.total ?? 0}</span>
              </p>
            </div>
          )}
        </section>

        <section>
          <h2 className="text-sm font-semibold text-gray-900 mb-3">Watchlist hits</h2>
          {hits.isPending ? (
            <p className="text-sm text-muted">Loading…</p>
          ) : hits.isError ? (
            <ErrorBanner title="Watchlist unreachable" message={(hits.error as Error).message} />
          ) : (
            <div className="bg-white border border-border rounded-xl p-4">
              <p className="text-sm">
                Active hits: <span className="font-bold" data-testid="total-hits">{hits.data?.total ?? 0}</span>
              </p>
            </div>
          )}
        </section>

        <section>
          <h2 className="text-sm font-semibold text-gray-900 mb-3">Blocked customers</h2>
          <div className="bg-white border border-border rounded-xl p-4 flex items-start gap-3">
            <ShieldAlert className="w-5 h-5 text-danger flex-shrink-0 mt-0.5" />
            <div>
              <p className="text-sm">
                AML-frozen: <span
                  className="font-bold"
                  data-testid="blocked-customers"
                >{summary.isPending ? '—' : (summary.data?.blockedCustomers ?? 0)}</span>
              </p>
              <p className="text-xs text-muted mt-0.5">
                KYC profiles where amlFrozen=true.
              </p>
            </div>
          </div>
        </section>
      </div>
    </div>
  );
}
